import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "sonner";

// Legacy Pages (keeping for backward compatibility)
import LandingPage from "./pages/LandingPage";
import LegacyLoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import OnboardingPage from "./pages/OnboardingPage";
import Dashboard from "./pages/Dashboard";
import EventsPage from "./pages/EventsPage";
import DirectoryPage from "./pages/DirectoryPage";
import RoadToSomaBay from "./pages/RoadToSomaBay";
import JobsPage from "./pages/JobsPage";
import TrainingPage from "./pages/TrainingPage";
import LegacyProtectedRoute from "./components/ProtectedRoute";

// New Auth System
import LoginPage from "./pages/auth/LoginPage";
import AuthCallback from "./pages/auth/AuthCallback";
import AcceptInvitationPage from "./pages/auth/AcceptInvitationPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";

// New CMS-Driven Public Pages
import HomePage from "./pages/public/HomePage";
import PlatformPage from "./pages/public/PlatformPage";
import ServicesPage from "./pages/public/ServicesPage";
import ServiceDetailPage from "./pages/public/ServiceDetailPage";
import MyGolfHubPage from "./pages/public/MyGolfHubPage";
import ProgramsPage from "./pages/public/ProgramsPage";
import PublicEventsPage from "./pages/public/EventsPage";
import EventsDirectoryPage from "./pages/public/EventsDirectoryPage";
import TemplateMarketplacePage from "./pages/public/TemplateMarketplacePage";
import SportsDirectoryPage from "./pages/public/SportsDirectoryPage";
import InsightsPage from "./pages/public/InsightsPage";
import ArticleDetailPage from "./pages/public/ArticleDetailPage";
import PublicAboutPage from "./pages/public/AboutPage";
import ContactPage from "./pages/public/ContactPage";
import FamilySportsDayPage from "./pages/public/FamilySportsDayPage";
import BecomeOrganizerPage from "./pages/public/BecomeOrganizerPage";

// Embed Widget
import EmbedEventWidget from "./pages/embed/EmbedEventWidget";

// Admin Pages
import EventConfigPage from "./pages/admin/EventConfigPage";
import AdminRegistrationsPage from "./pages/admin/AdminRegistrationsPage";
import AdminEventsListPage from "./pages/admin/AdminEventsListPage";
import AdminEventWizard from "./pages/admin/AdminEventWizard";
import CheckInScannerPage from "./pages/admin/CheckInScannerPage";
import EventAnalyticsDashboard from "./pages/admin/EventAnalyticsDashboard";
import StaffManagementPage from "./pages/admin/StaffManagementPage";
import BulkImportPage from "./pages/admin/BulkImportPage";
import OrganizerDashboard from "./pages/admin/OrganizerDashboard";

// Universal Events Public Page
import EventDetailPage from "./pages/public/EventDetailPage";
import TicketViewPage from "./pages/public/TicketViewPage";

// App Router with OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* New CMS-Driven Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
      <Route path="/mygolfhub" element={<MyGolfHubPage />} />
      <Route path="/programs" element={<ProgramsPage />} />
      <Route path="/events" element={<EventsDirectoryPage />} />
      <Route path="/events/legacy" element={<PublicEventsPage />} />
      <Route path="/events/:slug" element={<EventDetailPage />} />
      <Route path="/templates" element={<TemplateMarketplacePage />} />
      <Route path="/family-sports-day" element={<FamilySportsDayPage />} />
      
      {/* Embeddable Widget Route */}
      <Route path="/embed/events/:slug" element={<EmbedEventWidget />} />
      
      {/* Ticket View Route */}
      <Route path="/tickets/:ticketId" element={<TicketViewPage />} />
      
      {/* Organizer Application */}
      <Route path="/become-organizer" element={<BecomeOrganizerPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <OrganizerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/event-config" element={
        <ProtectedRoute>
          <EventConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/event-config/:eventId" element={
        <ProtectedRoute>
          <EventConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/registrations" element={
        <ProtectedRoute>
          <AdminRegistrationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/events" element={
        <ProtectedRoute>
          <AdminEventsListPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/events/new" element={
        <ProtectedRoute>
          <AdminEventWizard />
        </ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId" element={
        <ProtectedRoute>
          <AdminEventWizard />
        </ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/check-in" element={
        <ProtectedRoute>
          <CheckInScannerPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/analytics" element={
        <ProtectedRoute>
          <EventAnalyticsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/staff" element={
        <ProtectedRoute>
          <StaffManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/import" element={
        <ProtectedRoute>
          <BulkImportPage />
        </ProtectedRoute>
      } />
      
      {/* Public Directory & Content Routes */}
      <Route path="/directory" element={<SportsDirectoryPage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:slug" element={<ArticleDetailPage />} />
      <Route path="/about" element={<PublicAboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Legacy Routes */}
      <Route path="/onboarding" element={
        <LegacyProtectedRoute>
          <OnboardingPage />
        </LegacyProtectedRoute>
      } />
      <Route path="/somabay" element={<RoadToSomaBay />} />
      <Route path="/jobs" element={<JobsPage />} />
      <Route path="/training" element={<TrainingPage />} />
      
      {/* Protected Dashboard Routes (Legacy) */}
      <Route path="/dashboard/*" element={
        <LegacyProtectedRoute>
          <Dashboard />
        </LegacyProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App noise-bg min-h-screen">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: 'hsl(0 0% 4%)',
              border: '1px solid hsl(240 4% 16%)',
              color: 'white'
            }
          }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;
